import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [entitlement, setEntitlement] = useState(null);
    const [loading, setLoading] = useState(true);

    const persist = useCallback((token, userData) => {
        localStorage.setItem(TOKEN_KEY, token);
        setUser(userData);
    }, []);

    const refreshEntitlement = useCallback(async () => {
        if (!localStorage.getItem(TOKEN_KEY)) {
            setEntitlement(null);
            return null;
        }
        try {
            const { data } = await api.get("/entitlement/me");
            setEntitlement(data);
            return data;
        } catch {
            return null;
        }
    }, []);

    const refresh = useCallback(async () => {
        if (!localStorage.getItem(TOKEN_KEY)) {
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            await refreshEntitlement();
        } catch {
            localStorage.removeItem(TOKEN_KEY);
            setUser(null);
            setEntitlement(null);
        } finally {
            setLoading(false);
        }
    }, [refreshEntitlement]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(
        async (email, password) => {
            const { data } = await api.post("/auth/login", { email, password });
            persist(data.token, data.user);
            refreshEntitlement();
            return data.user;
        },
        [persist, refreshEntitlement],
    );

    const register = useCallback(
        async (name, email, password) => {
            const { data } = await api.post("/auth/register", { name, email, password });
            persist(data.token, data.user);
            refreshEntitlement();
            return data.user;
        },
        [persist, refreshEntitlement],
    );

    const startTrial = useCallback(async () => {
        const { data } = await api.post("/entitlement/start-trial");
        await refreshEntitlement();
        return data;
    }, [refreshEntitlement]);

    const logout = useCallback(() => {
        api.post("/auth/logout").catch(() => {});
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
        setEntitlement(null);
    }, []);

    const deleteAccount = useCallback(async () => {
        await api.delete("/auth/account");
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
        setEntitlement(null);
    }, []);

    return (
        <AuthContext.Provider
            value={{
                user,
                entitlement,
                loading,
                login,
                register,
                logout,
                deleteAccount,
                refresh,
                refreshEntitlement,
                startTrial,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
